import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import ms from 'ms';
import { Fragment, ReactNode, useCallback } from 'react';

import { ShippingMethods } from '@/enums/shippingMethod';
import {
    paymentStatusPrimaryMessages,
    paymentStatusSecondaryMessages,
} from '@/pages/WonItems/components/WonItemsCard/components/StatusBadge/helpers/badgeTextMessages';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogAddress } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { openShippingAndPaymentModal } from '@/redux/modules/modal';
import { WonCatalog } from '@/types/WonCatalog';
import { createFullAddressString } from '@/utils/address/createAddressString';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type GetWonCatalogStatusParams = {
    addShippingDate?: Date;
    catalogId: number;
    invoiceId?: number;
    lastPaymentDate?: Date;
    primaryStatus: string;
    secondaryStatus?: string;
    shippingAmount?: number;
    shippingAvailable?: boolean;
    shippingCurrency?: string;
    shippingMethod?: ShippingMethods;
    wonCatalog: WonCatalog;
};

export const useGetWonCatalogStatus = ({
    addShippingDate,
    catalogId,
    invoiceId,
    lastPaymentDate,
    primaryStatus,
    secondaryStatus,
    shippingAmount,
    shippingAvailable,
    shippingCurrency,
    shippingMethod,
    wonCatalog,
}: GetWonCatalogStatusParams): {
    primaryText: ReactNode;
    secondaryText: ReactNode;
} => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));

    const isPaid = primaryStatus === 'paid' || primaryStatus === 'Invoice Paid';

    const openShipAndPayModal = useCallback(() => {
        dispatch(openShippingAndPaymentModal());
    }, [dispatch]);

    let primaryText: ReactNode;
    let secondaryText: ReactNode;

    switch (shippingMethod) {
        case ShippingMethods.LiveShipping:
            if (primaryStatus === 'Order Created') {
                primaryText = paymentStatusPrimaryMessages[primaryStatus];
                secondaryText = paymentStatusSecondaryMessages.paymentPending;
            } else {
                primaryText = primaryStatus;
                secondaryText = secondaryStatus;
            }
            break;
        case ShippingMethods.BidderShipping:
            primaryText = paymentStatusPrimaryMessages[primaryStatus];
            secondaryText = paymentStatusSecondaryMessages[ShippingMethods.BidderShipping];
            break;
        case ShippingMethods.Pickup:
            primaryText = paymentStatusPrimaryMessages[primaryStatus];
            secondaryText = paymentStatusSecondaryMessages[ShippingMethods.Pickup](
                createFullAddressString(
                    address.address1,
                    address.city,
                    address.state,
                    address.postalCode,
                    address.country,
                    address.address2
                )
            );
            break;
        default:
            break;
    }

    if (invoiceId === 0) {
        primaryText = paymentStatusPrimaryMessages.invoicePending;
    }

    if (isPaid && shippingAvailable && Boolean(addShippingDate)) {
        const withinAddShippingDeadline = addShippingDate.getTime() > Date.now();

        if (withinAddShippingDeadline) {
            secondaryText = paymentStatusSecondaryMessages['addShippingEligible'](
                shippingAmount,
                shippingCurrency,
                addShippingDate
            );
        }

        if (!withinAddShippingDeadline) {
            secondaryText = paymentStatusSecondaryMessages['addShippingIneligible'](openShipAndPayModal);
        }
    }

    if (
        (primaryStatus === 'Invoice Pending' || primaryStatus === 'Order Created') &&
        !isPaid &&
        ShippingMethods.LiveShipping
    ) {
        primaryText = paymentStatusPrimaryMessages['OrderCreated'];
        secondaryText = paymentStatusSecondaryMessages['youWillSeeStatus'];
    }

    if (!primaryText) {
        const wasPaidTwoWeeksAgo =
            Boolean(lastPaymentDate) && lastPaymentDate.getTime() > 0
                ? Date.now() - lastPaymentDate.getTime() > ms('2 weeks')
                : false;
        const messageKey = wasPaidTwoWeeksAgo ? 'orderComplete' : primaryStatus;
        primaryText = paymentStatusPrimaryMessages[messageKey] || primaryStatus;
    }

    if (!secondaryText && !shippingAvailable) {
        const wasPaidTwoWeeksAgo =
            Boolean(lastPaymentDate) && lastPaymentDate.getTime() > 0
                ? Date.now() - lastPaymentDate.getTime() > ms('2 weeks')
                : false;

        let messageKey = primaryStatus;
        if (wasPaidTwoWeeksAgo) {
            messageKey = 'orderComplete';
        } else if (isPaid && !shippingMethod) {
            messageKey = ShippingMethods.BidderShipping;
        }

        secondaryText = paymentStatusSecondaryMessages[secondaryStatus || messageKey] || secondaryStatus;
    }

    if (shippingMethod === ShippingMethods.HouseShipping) {
        if (wonCatalog.invoice.trackingInformation?.length) {
            primaryText = primaryStatus;

            const trackingLines = wonCatalog.invoice.trackingInformation?.reduce((aggregate, info, index) => {
                let trackingContent: ReactNode[] = [
                    <BodySecondary
                        color="secondary"
                        key={`in-house-shipping.tracking-info.carrier-name.${index}`}
                    >
                        {info.carrierName}
                    </BodySecondary>,
                ];
                if (info.trackingNumber) {
                    trackingContent.push(
                        info.trackingLink ? (
                            <Link
                                href={info.trackingLink}
                                key={`in-house-shipping.tracking-info.tracking-number-link.${index}`}
                                size="sm"
                                target="_blank"
                            >
                                {info.trackingNumber}
                            </Link>
                        ) : (
                            <Fragment key={`in-house-shipping.tracking-info.tracking-number.${index}`}>
                                {info.trackingNumber}
                            </Fragment>
                        )
                    );
                }
                // because a shipment needs a tracking number or a link to be saved, this can be just an if-else
                else {
                    trackingContent.push(
                        <Link
                            href={info.trackingLink}
                            key={`in-house-shipping.tracking-info.tracking-link.${index}`}
                            size="sm"
                            target="_blank"
                        >
                            {formatMessage(['wonitems', 'invoiceTrackingInfo', 'trackingLink'])}
                        </Link>
                    );
                }

                return [
                    ...aggregate,
                    <div
                        className="flex gap-2xs"
                        key={`in-house-shipping.tracking-info.wrapper.${index}`}
                    >
                        {trackingContent}
                    </div>,
                ];
            }, [] as ReactNode[]);

            secondaryText = (
                <>
                    {secondaryText}
                    <br />
                    <div className="flex gap-2xs">
                        <BodySecondary color="secondary">
                            {formatMessage(['wonitems', 'invoiceTrackingInfo', 'trackingNumberTitle'])}
                        </BodySecondary>
                        <div className="flex flex-col">{trackingLines}</div>
                    </div>
                </>
            );
        }
    }

    return { primaryText, secondaryText };
};
